import {
  Component,
  HostBinding,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { NavItem } from './nav-item';
import { Router } from '@angular/router';
import { NavService } from '../../../../../services/nav.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { Subject, Subscription } from 'rxjs';

export const accordionSubject = new Subject<NavItem>();

@Component({
  selector: 'app-nav-item',
  standalone: true,
  imports: [TranslateModule, TablerIconsModule, MaterialModule, CommonModule],
  templateUrl: './nav-item.component.html',
  styles: [`
    /* =========================================
       DISEÑO BASE CLARO Y LIMPIO
       ========================================= */
    .menu-list-item {
      position: relative;
      border-radius: 8px !important;
      margin: 4px 16px 4px 12px !important; 
      padding: 10px 32px 10px 0 !important;
      transition: all 0.2s ease-in-out !important;
      background-color: transparent !important;
      color: #334155 !important;
      cursor: pointer;
      display: flex;
      align-items: center;
      min-height: 44px;
      opacity: 1 !important;
    }

    /* 💡 MAGIA DE LA INDENTACIÓN: Movemos TODO el bloque hacia la derecha */
    .item-content {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0; 
      overflow: hidden;
      /* Aplica un salto masivo de 20px por cada nivel de submenú */
      padding-left: calc(8px + (var(--indent-level, 0) * 10px)) !important;
    }

    .routeIcon {
      margin-right: 12px;
      font-size: 20px;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      color: #64748b !important;
      transition: all 0.2s;
    }

    .hide-menu {
      font-weight: 500;
      font-size: 13.5px;
      letter-spacing: 0.3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .arrow-icon {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      color: #94a3b8;
    }

    /* =========================================
       ESTADOS INTERACTIVOS (UX)
       ========================================= */

    .menu-list-item:hover {
      background-color: #f1f5f9 !important;
      color: #1976d2 !important;
    }
    .menu-list-item:hover .routeIcon,
    .menu-list-item:hover .arrow-icon {
      color: #1976d2 !important; 
    }

    .menu-list-item.expanded {
      background-color: #e3f2fd !important;
      color: #1976d2 !important;
      font-weight: 600;
    }
    .menu-list-item.expanded .routeIcon,
    .menu-list-item.expanded .arrow-icon {
      color: #1976d2 !important;
    }

    .menu-list-item.activeMenu {
      background-color: #1976d2 !important;
      color: #ffffff !important;
      font-weight: 600;
      box-shadow: 0 4px 8px rgba(25, 118, 210, 0.25) !important;
    }
    .menu-list-item.activeMenu .routeIcon {
      color: #ffffff !important; 
    }

    /* =========================================
       SUBMENÚS (Efecto Árbol Opcional)
       ========================================= */
    .menu-list-item.is-child {
      min-height: 40px;
      margin-top: 2px !important;
      margin-bottom: 2px !important;
    }

    .sub-menu {
      position: relative;
      margin-top: 4px;
      margin-bottom: 8px;
    }
    
    /* Línea vertical para agrupar visualmente los submenús */
    .sub-menu::before {
      content: '';
      position: absolute;
      left: 26px; /* Centrado bajo el ícono del padre */
      top: 0;
      bottom: 12px;
      width: 2px;
      background-color: #e2e8f0;
      border-radius: 2px;
    }
  `],
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4,0.0,0.2,1)')),
    ]),
  ],
})
export class AppNavItemComponent implements OnInit, OnChanges, OnDestroy {
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() notify: EventEmitter<boolean> = new EventEmitter<boolean>();

  expanded: any = false;
  disabled: any = false;
  twoLines: any = false;
  @HostBinding('attr.aria-expanded') ariaExpanded = this.expanded;
  @Input() item: NavItem | any;
  @Input() depth: any;

  private accordionSub!: Subscription;

  constructor(public navService: NavService, public router: Router) {
    if (this.depth === undefined) {
      this.depth = 0;
    }
  }

  ngOnInit() {
    this.accordionSub = accordionSubject.subscribe((clickedItem: NavItem) => {
      if (this.item !== clickedItem && this.expanded) {
        if (!this.isDescendant(this.item, clickedItem)) {
          this.expanded = false;
          this.ariaExpanded = false;
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.accordionSub) {
      this.accordionSub.unsubscribe();
    }
  }

  ngOnChanges() {
    this.navService.currentUrl.subscribe((url: string) => {
      if (this.item.route && url) {
        this.expanded = url.indexOf(`/${this.item.route}`) === 0;
        this.ariaExpanded = this.expanded;
      }
    });
  }

  isDescendant(parent: NavItem, child: NavItem): boolean {
    if (!parent.children) return false;
    if (parent.children.includes(child)) return true;
    return parent.children.some(c => this.isDescendant(c, child));
  }

  onItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      this.router.navigate([item.route]);
    }
    
    if (item.children && item.children.length) {
      this.expanded = !this.expanded;
      if (this.expanded) {
        accordionSubject.next(this.item); 
      }
    }
    
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    
    if (!item.children || !item.children.length) {
      this.notify.emit(true);
    }
  }
}